import { Box, Container, Grid, Divider, IconButton } from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ScheduleIcon from "@mui/icons-material/Schedule";
import MKTypography from "components/MKTypography";
import logoCT from "assets/images/LogoLarge.png";

const date = new Date().getFullYear();

// Footer configuration data (separate from component)
export const footerConfig = {
  brand: {
    name: "Scuba Adicts",
    image: logoCT,
    route: "/",
    description: "Your premier diving experience in Sri Lanka",
  },
  contact: {
    phone: "+94 774 570 342",
    email: "dive@scubaaddicts.lk",
    address: "65 Nelumpura Road Moratuwa. Sri Lanka.",
    hours: "Open daily: 7:00 AM - 7:00 PM",
  },
  menus: [
    {
      name: "Company",
      items: [
        { name: "About Us", href: "/#/pages/landing-pages/about-us" },
        { name: "Our Team", href: "/#/pages/landing-pages/our-team" },
        { name: "Contact Us", href: "/#/pages/landing-pages/contact-us" },
        { name: "Gallery", href: "/#/pages/landing-pages/gallery" },
      ],
    },
    {
      name: "Dive Locations",
      items: [
        { name: "East Coast Season", href: "/#/pages/landing-pages/locations/east-coast" },
        { name: "West Coast Season", href: "/#/pages/landing-pages/locations/west-coast" },
      ],
    },
  ],
  socials: [
    {
      icon: <FacebookIcon />,
      link: "https://www.facebook.com/scubaaddictssrilanka",
      name: "Facebook",
    },
    {
      icon: <InstagramIcon />,
      link: "https://www.instagram.com/scubaaddicts.sl/",
      name: "Instagram",
    },
  ],
};

// Footer Component
const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "background.default",
        pt: 6,
        pb: 2,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Brand and contact info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src={footerConfig.brand.image}
                alt={footerConfig.brand.name}
                sx={{ height: 100, mb: 2 }}
              />
              <MKTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {footerConfig.brand.description}
              </MKTypography>
            </Box>

            <Box>
              <MKTypography variant="h6" gutterBottom>
                Follow Us
              </MKTypography>
              <Box>
                {footerConfig.socials.map((social, index) => (
                  <IconButton
                    key={index}
                    component="a"
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.name}
                    sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Menu links */}
          {footerConfig.menus.map((menu, index) => (
            <Grid item xs={6} md={2} key={index}>
              <MKTypography variant="h6" gutterBottom>
                {menu.name}
              </MKTypography>
              <Box>
                {menu.items.map((item, itemIndex) => (
                  <MKTypography
                    key={itemIndex}
                    component="a"
                    href={item.href}
                    variant="body2"
                    display="block"
                    gutterBottom
                    sx={{
                      color: "text.secondary",
                      textDecoration: "none",
                      "&:hover": { color: "primary.main", textDecoration: "underline" },
                    }}
                  >
                    {item.name}
                  </MKTypography>
                ))}
              </Box>
            </Grid>
          ))}
          <Grid item xs={12} md={4}>
            <MKTypography variant="h6" gutterBottom>
              Contact Info
            </MKTypography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <PhoneIcon sx={{ mr: 1, fontSize: "1rem", color: "primary.main" }} />
              <MKTypography variant="body2" color="text.secondary">
                {footerConfig.contact.phone}
              </MKTypography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <EmailIcon sx={{ mr: 1, fontSize: "1rem", color: "primary.main" }} />
              <MKTypography variant="body2" color="text.secondary">
                {footerConfig.contact.email}
              </MKTypography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <LocationOnIcon sx={{ mr: 1, fontSize: "1rem", color: "primary.main" }} />
              <MKTypography variant="body2" color="text.secondary">
                {footerConfig.contact.address}
              </MKTypography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <ScheduleIcon sx={{ mr: 1, fontSize: "1rem", color: "primary.main" }} />
              <MKTypography variant="body2" color="text.secondary">
                {footerConfig.contact.hours}
              </MKTypography>
            </Box>
          </Grid>

          {/* Newsletter subscription */}
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Copyright and legal links */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: 2,
          }}
        >
          <MKTypography variant="button" fontWeight="regular">
            &copy; {date} Scuba Adicts. All rights reserved.
          </MKTypography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
